import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  browseLists,
  joinOrRequest,
  leaveList,
  getMyListCommitments,
  getMyMemberships,
  getMyRequests,
  getMyInvites,
  acceptInvite,
  declineInvite,
} from "../../api/roster";
import {
  getOpportunities,
  applyToSlot,
  withdrawApplication,
  getMyApplications,
} from "../../api/opportunities";
import { backendUrl } from "../../api/constants";
import { modal } from "../../util/useModal";
import RosterCommitmentsConfirm from "../../components/RosterCommitmentsConfirm/RosterCommitmentsConfirm";
import ProfilePicture from "../../components/icons/ProfilePicture";
import { enqueueSnackbar } from "notistack";
import { DateTime } from "luxon";
import { FiCalendar, FiList, FiSearch } from "react-icons/fi";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../context/UserContext/UserProvider";
import {
  formatEventDate,
  formatOccurrenceDate,
  formatSlotOccurrenceDate,
  formatSlotOccurrenceTimeRange,
  getSlotOccurrenceDateTime,
  toOccurrenceDateTime,
} from "../../util/eventDateTime";
import AppCalendar from "../../components/AppCalendar/AppCalendar";
import {
  useNotificationActions,
  useNotificationActivity,
} from "../../context/NotificationsContext/NotificationsProvider";

// Sections, shown as sub-headings under the dashboard's "rosters" header for artists (portalcontext.jsx).
// The dashboard's ?rostersView= param picks one; notification targets use these values.
export const ROSTER_VIEWS = ["opportunities", "lists", "browse"];
export const DEFAULT_ROSTER_VIEW = "opportunities";
// Sub-tabs inside "my lists"
const MY_LISTS_SUB_TABS = ["joined", "invites", "requests"];

// visiting a section clears its unread notifications
const VIEW_NOTIFICATION_SCOPES = {
  opportunities: "roster:opportunities",
  lists: "roster:memberships",
};

// rendered as the dashboard's "rosters" tab; artists pick a section from the menu (rosters → opportunities / my lists / browse)
const RosterPage = () => {
  const [searchParams] = useSearchParams();
  const viewFromUrl = searchParams.get("rostersView");
  const activeView = ROSTER_VIEWS.includes(viewFromUrl) ? viewFromUrl : DEFAULT_ROSTER_VIEW;
  const navigate = useNavigate();
  const { user } = useUser();
  const { markScopeRead } = useNotificationActions();

  useEffect(() => {
    const scope = VIEW_NOTIFICATION_SCOPES[activeView];
    if (scope && markScopeRead) markScopeRead(scope);
  }, [activeView, markScopeRead]);

  if (user && user.accountType === "user") {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="w-full">
      {activeView === "lists" && <MyListsPage />}
      {activeView === "opportunities" && <OpportunitiesPage />}
      {activeView === "browse" && (
        <div className="px-kit-24">
          <BrowseTab />
        </div>
      )}
    </div>
  );
};

// old /roster links (notifications, emails, bookmarks) → the dashboard's rosters tab
export const RosterRedirect = () => {
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  const view = params.get("tab");
  params.delete("tab");
  params.set("tab", "rosters");
  if (ROSTER_VIEWS.includes(view)) params.set("rostersView", view);
  return <Navigate to={`/dashboard?${params.toString()}`} replace />;
};

// ─── My Lists page (with sub-tabs) ───────────────────────────────────────────

const MyListsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activity = useNotificationActivity();
  const initialSubTab = MY_LISTS_SUB_TABS.includes(searchParams.get("listTab"))
    ? searchParams.get("listTab")
    : "joined";
  const [subTab, setSubTab] = useState(initialSubTab);
  const [joinedCount, setJoinedCount] = useState(null);
  const [invitesCount, setInvitesCount] = useState(null);
  const [requestsCount, setRequestsCount] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshCounts = useCallback(() => {
    getMyMemberships().then((d) => setJoinedCount(d.memberships.length)).catch(() => {});
    getMyInvites().then((d) => setInvitesCount(d.invites.length)).catch(() => {});
    getMyRequests().then((d) => setRequestsCount(d.requests.length)).catch(() => {});
  }, []);

  useEffect(() => {
    const tabFromUrl = searchParams.get("listTab");
    if (MY_LISTS_SUB_TABS.includes(tabFromUrl)) setSubTab(tabFromUrl);
  }, [searchParams]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  useEffect(() => {
    const notification = activity?.notification;
    if (!activity?.version || notification?.scope !== "roster:memberships") return;
    refreshCounts();
    setRefreshKey((key) => key + 1);
  }, [activity?.version, refreshCounts]);

  const subTabCount = (tab) => {
    if (tab === "joined") return joinedCount;
    if (tab === "invites") return invitesCount;
    if (tab === "requests") return requestsCount;
    return null;
  };

  const setListsSubTab = (tab) => {
    setSubTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "rosters");
      next.set("rostersView", "lists");
      next.set("listTab", tab);
      return next;
    });
  };

  return (
    <div className="flex flex-col">
      {/* Sub-tab bar */}
      <div className="flex gap-kit-16 px-kit-24 pt-kit-16 pb-kit-8">
        {MY_LISTS_SUB_TABS.map((tab) => {
          const count = subTabCount(tab);
          const active = subTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setListsSubTab(tab)}
              className={`flex items-center gap-kit-6 font-bold cursor-pointer whitespace-nowrap duration-150 text-kit-base px-kit-16 py-kit-8 rounded-kit-2xl shadow-md ${
                active
                  ? "bg-[var(--inyc-primary-text-color)] text-[var(--inyc-primary)]"
                  : "text-[var(--inyc-primary-text-color)]/70 border-[1px] border-[var(--inyc-input-border-color)]"
              }`}
            >
              {tab}
              {count !== null && count > 0 && (
                <span
                  style={{ color: "inherit", opacity: 0.6, fontSize: "var(--inyc-text-2xs)" }}
                  className="font-['Primary-Medium']"
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="px-kit-24">
        {subTab === "joined" && <MyMembershipsTab refreshKey={refreshKey} />}
        {subTab === "invites" && (
          <MyInvitesTab
            refreshKey={refreshKey}
            onAccepted={() => {
              setInvitesCount((count) => Math.max((count || 1) - 1, 0));
              setJoinedCount((count) => (count || 0) + 1);
            }}
            onDeclined={() => setInvitesCount((count) => Math.max((count || 1) - 1, 0))}
          />
        )}
        {subTab === "requests" && <MyRequestsTab refreshKey={refreshKey} />}
      </div>
    </div>
  );
};

// ─── Joined memberships ───────────────────────────────────────────────────────

const MyMembershipsTab = ({ refreshKey = 0 }) => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getMyMemberships();
        setMemberships(data.memberships);
      } catch {
        enqueueSnackbar("Failed to load memberships", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [refreshKey]);

  const handleLeave = async (listId, rosterName) => {
    let commitments = [];
    try {
      ({ commitments = [] } = await getMyListCommitments(listId));
    } catch {
      enqueueSnackbar("Failed to check your bookings", { variant: "error" });
      return;
    }

    const confirmed = await modal(({ proceed, cancel }) => (
      <RosterCommitmentsConfirm
        title={`leave ${rosterName}?`}
        message="you're still on upcoming slots from this roster. leaving will"
        emptyMessage="you'll stop seeing this roster's opportunities. you can ask to join again later."
        confirmLabel="leave"
        commitments={commitments}
        onCancel={cancel}
        onConfirm={proceed}
      />
    ), { raw: true });
    if (!confirmed) return;

    try {
      await leaveList(listId);
      setMemberships((prev) => prev.filter((m) => m._id !== listId));
      enqueueSnackbar("Left roster", { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to leave roster", { variant: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      {loading ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">loading...</div>
      ) : memberships.length === 0 ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
          you're not on any rosters yet
        </div>
      ) : (
        memberships.map((m) => (
          <RosterCard
            key={m._id}
            list={{ ...m, memberState: "member" }}
            onLeave={() => handleLeave(m._id, m.displayName)}
            joinedAt={m.joinedAt}
          />
        ))
      )}
    </div>
  );
};

// ─── Pending invites ─────────────────────────────────────────────────────────

const MyInvitesTab = ({ refreshKey = 0, onAccepted, onDeclined }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingToken, setActingToken] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getMyInvites();
        setInvites(data.invites || []);
      } catch {
        enqueueSnackbar("Failed to load invites", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [refreshKey]);

  const removeInvite = (token) => {
    setInvites((prev) => prev.filter((invite) => invite.token !== token));
  };

  const handleAccept = async (invite) => {
    setActingToken(invite.token);
    try {
      await acceptInvite(invite.token);
      removeInvite(invite.token);
      onAccepted?.();
      enqueueSnackbar("Joined roster", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to accept invite", { variant: "error" });
    } finally {
      setActingToken(null);
    }
  };

  const handleDecline = async (invite) => {
    setActingToken(invite.token);
    try {
      await declineInvite(invite.token);
      removeInvite(invite.token);
      onDeclined?.();
      enqueueSnackbar("Invite declined", { variant: "info" });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to decline invite", { variant: "error" });
    } finally {
      setActingToken(null);
    }
  };

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      {loading ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">loading...</div>
      ) : invites.length === 0 ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
          no roster invites
        </div>
      ) : (
        invites.map((invite) => (
          <div
            key={invite.token}
            className="flex items-center gap-kit-12 rounded-[var(--inyc-input-border-radius)] border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] px-kit-12 py-kit-12"
          >
            <ProfilePicture
              username={invite.displayName}
              profilePicture={invite.displayPicture}
              style={{ width: "2.8rem", height: "2.8rem", fontSize: "var(--inyc-text-2xs)", flexShrink: 0 }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate font-['Primary-Medium'] text-kit-sm">{invite.displayName}</div>
              {invite.invitedAt && (
                <div className="mt-kit-2 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                  invited {DateTime.fromISO(invite.invitedAt).toRelative()}
                </div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-kit-8">
              <button
                type="button"
                onClick={() => handleAccept(invite)}
                disabled={actingToken === invite.token}
                className="rounded-kit-full bg-[var(--inyc-secondary)] px-kit-16 py-kit-6 font-['Primary-Medium'] text-kit-xs text-[var(--inyc-secondary-text-color)] transition-opacity duration-150 hover:opacity-80 disabled:opacity-50"
              >
                {actingToken === invite.token ? "..." : "accept"}
              </button>
              <button
                type="button"
                onClick={() => handleDecline(invite)}
                disabled={actingToken === invite.token}
                className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-transparent px-kit-16 py-kit-6 font-['Primary-Medium'] text-kit-xs text-[var(--inyc-primary-text-color-light)] transition-opacity duration-150 hover:opacity-80 disabled:opacity-50"
              >
                decline
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Pending requests ─────────────────────────────────────────────────────────

const MyRequestsTab = ({ refreshKey = 0 }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getMyRequests();
        setRequests(data.requests);
      } catch {
        enqueueSnackbar("Failed to load requests", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [refreshKey]);

  const handleWithdraw = async (listId) => {
    try {
      await leaveList(listId);
      setRequests((prev) => prev.filter((r) => r._id !== listId));
      enqueueSnackbar("Request withdrawn", { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to withdraw request", { variant: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      {loading ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">loading...</div>
      ) : requests.length === 0 ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
          no pending requests
        </div>
      ) : (
        requests.map((r) => (
          <div
            key={r._id}
            className="flex items-center gap-kit-12 py-kit-12 px-kit-12 rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)]"
          >
            <ProfilePicture
              username={r.displayName}
              profilePicture={r.displayPicture}
              style={{ width: "2.8rem", height: "2.8rem", fontSize: "var(--inyc-text-2xs)", flexShrink: 0 }}
            />
            <div className="flex-1 min-w-0">
              <div className="font-['Primary-Medium'] text-kit-sm truncate">{r.displayName}</div>
              {r.requestedAt && (
                <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] mt-kit-2">
                  requested {DateTime.fromISO(r.requestedAt).toRelative()}
                </div>
              )}
              <span className="text-kit-2xs px-kit-6 py-kit-2 rounded-kit-full bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)] mt-kit-4 inline-block">
                pending
              </span>
            </div>
            <button
              onClick={() => handleWithdraw(r._id)}
              className="shrink-0 px-kit-16 py-kit-6 rounded-kit-full text-kit-xs font-['Primary-Medium'] bg-transparent border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:opacity-80 transition-opacity duration-150"
            >
              withdraw
            </button>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Browse Tab ──────────────────────────────────────────────────────────────

const BrowseTab = () => {
  const [lists, setLists] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const LIMIT = 20;

  const fetchLists = useCallback(
    async (pageNum = 1, reset = false) => {
      pageNum === 1 ? setLoading(true) : setLoadingMore(true);
      try {
        const data = await browseLists({ search, page: pageNum, limit: LIMIT });
        setLists((prev) => (reset || pageNum === 1 ? data.lists : [...prev, ...data.lists]));
        setTotal(data.total);
        setPage(pageNum);
      } catch {
        enqueueSnackbar("Failed to load rosters", { variant: "error" });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search]
  );

  useEffect(() => {
    fetchLists(1, true);
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleRequest = async (list) => {
    if (list.memberState !== "none") return;
    try {
      const data = await joinOrRequest(list._id);
      const nextState = data.status === "member" ? "member" : "pending";
      setLists((prev) =>
        prev.map((l) =>
          l._id === list._id
            ? {
                ...l,
                memberState: nextState,
                memberCount:
                  nextState === "member" && typeof l.memberCount === "number"
                    ? l.memberCount + 1
                    : l.memberCount,
              }
            : l
        )
      );
      enqueueSnackbar(
        nextState === "member" ? "Joined roster" : "Request sent",
        { variant: "success" }
      );
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to join roster";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      <form onSubmit={handleSearch} className="flex gap-kit-12">
        <div className="flex-1 flex items-center gap-kit-8 border-[1px] border-[var(--inyc-input-border-color)] rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] px-kit-16">
          <FiSearch size={15} className="text-[var(--inyc-primary-text-color-light)] shrink-0" />
          <input
            className="flex-1 bg-transparent py-kit-10 text-kit-sm outline-none"
            placeholder="search rosters..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </form>

      {loading ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">loading...</div>
      ) : lists.length === 0 ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">no rosters found</div>
      ) : (
        <>
          {lists.map((list) => (
            <RosterCard key={list._id} list={list} onRequest={handleRequest} />
          ))}
          {lists.length < total && (
            <button
              onClick={() => fetchLists(page + 1)}
              disabled={loadingMore}
              className="mt-kit-8 py-kit-10 text-kit-sm text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)] transition-colors duration-150"
            >
              {loadingMore ? "loading..." : "load more"}
            </button>
          )}
        </>
      )}
    </div>
  );
};

// ─── Roster Card ─────────────────────────────────────────────────────────────

const RosterCard = ({ list, onRequest, onLeave, joinedAt }) => {
  const state = list.memberState || "none";
  const joinPolicy = list.joinPolicy || (list.type === "public" ? "open" : "closed");
  const actionLabel = joinPolicy === "open" ? "join" : "request";

  return (
    <div className="flex items-center gap-kit-12 py-kit-12 px-kit-12 rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)]">
      <ProfilePicture
        username={list.displayName}
        profilePicture={list.displayPicture}
        style={{ width: "2.8rem", height: "2.8rem", fontSize: "var(--inyc-text-2xs)", flexShrink: 0 }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-['Primary-Medium'] text-kit-sm truncate">{list.displayName}</div>
        <div className="flex items-center gap-kit-8 mt-kit-2">
          {list.memberCount !== undefined && (
            <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
              {list.memberCount} {list.memberCount === 1 ? "member" : "members"}
            </span>
          )}
        </div>
        {joinedAt && (
          <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] mt-kit-2">
            joined {DateTime.fromISO(joinedAt).toRelative()}
          </div>
        )}
      </div>

      {onRequest && state === "none" && (
        <button
          onClick={() => onRequest(list)}
          className="shrink-0 px-kit-16 py-kit-6 rounded-kit-full text-kit-xs font-['Primary-Medium'] bg-[var(--inyc-accent)] text-white hover:opacity-80 transition-opacity duration-150"
        >
          {actionLabel}
        </button>
      )}
      {onRequest && state === "pending" && (
        <span className="shrink-0 px-kit-16 py-kit-6 rounded-kit-full text-kit-xs font-['Primary-Medium'] bg-transparent border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)]">
          requested
        </span>
      )}
      {onRequest && state === "member" && (
        <span className="shrink-0 px-kit-16 py-kit-6 rounded-kit-full text-kit-xs font-['Primary-Medium'] bg-transparent border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)]">
          member
        </span>
      )}

      {onLeave && (
        <button
          onClick={onLeave}
          className="shrink-0 px-kit-16 py-kit-6 rounded-kit-full text-kit-xs font-['Primary-Medium'] bg-transparent border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:opacity-80 transition-opacity duration-150"
        >
          leave
        </button>
      )}
    </div>
  );
};

// ─── Opportunities ────────────────────────────────────────────────────────────

const OPPORTUNITY_PAGES = ["booked", "applied", "open slots"];
const APPLIED_FILTERS = ["waiting", "past decisions"];
// deep-link params (?oppPage=booked|applied|open&appliedFilter=past) → UI labels
const OPPORTUNITY_PAGE_PARAMS = { booked: "booked", applied: "applied", open: "open slots" };
const APPLIED_FILTER_PARAMS = { waiting: "waiting", past: "past decisions" };

/**
 * Opens the event night named by ?focusEventId=&focusOccurrenceStart= (set by
 * notification links) once it's in the list, then clears the params.
 */
const useOpportunityFocus = ({ items, loading, setExpandedEvent, setSelectedItemId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusEventId = searchParams.get("focusEventId");
  const focusOccurrenceStart = searchParams.get("focusOccurrenceStart");

  useEffect(() => {
    if (!focusEventId || loading) return;
    const focusMs = focusOccurrenceStart ? new Date(focusOccurrenceStart).getTime() : null;
    const match = items.find(
      (item) =>
        String(item.event?._id) === String(focusEventId) &&
        (!focusMs || new Date(item.occurrenceStart).getTime() === focusMs)
    );
    if (match) {
      const groupKey = getOccurrenceGroupKey(match);
      setExpandedEvent(groupKey);
      setSelectedItemId(match.id);
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-group-key="${groupKey}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("focusEventId");
      next.delete("focusOccurrenceStart");
      return next;
    }, { replace: true });
  }, [focusEventId, focusOccurrenceStart, items, loading]);
};
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const ACTIVE_APPLICATION_STATUSES = new Set(["pending", "accepted"]);

const slotOccurrenceKey = (slotId, occurrenceStart) =>
  `${slotId}:${new Date(occurrenceStart).getTime()}`;

const getImageSource = (image) => {
  const raw =
    typeof image === "string"
      ? image
      : image?.url || image?.original || image?.base64 || image?.path || "";
  if (!raw) return null;
  if (raw.startsWith("http") || raw.startsWith("data:") || raw.startsWith("blob:")) {
    return raw;
  }
  return `${backendUrl}/assets/${raw}`;
};

const getEventCoverImage = (event) => {
  const image = event?.images?.find(Boolean);
  return getImageSource(image);
};

const statusStyle = {
  pending: "bg-warning-soft text-warning",
  accepted: "bg-success-soft text-success",
  declined: "bg-danger-soft text-danger",
  cancelled: "bg-neutral-soft text-neutral",
};

const statusToCalendarClass = (status) => {
  if (status === "accepted") return "active";
  if (status === "pending") return "awaiting-review";
  return "rejected";
};

const flattenOpenSlotItems = (events = []) => {
  const items = [];
  for (const event of events) {
    for (const occurrence of event.occurrences || []) {
      for (const slot of occurrence.slots || []) {
        if (
          slot.myApplication &&
          ACTIVE_APPLICATION_STATUSES.has(slot.myApplication.status)
        ) {
          continue;
        }
        items.push({
          id: `${event._id}:${slot._id}:${new Date(occurrence.occurrenceStart).getTime()}`,
          event,
          slot,
          occurrenceStart: occurrence.occurrenceStart,
          occurrenceEnd: occurrence.occurrenceEnd,
          variant: "open",
        });
      }
    }
  }
  return items;
};

const flattenApplicationItems = (applications = [], filterFn = () => true) =>
  applications.filter(filterFn).map((application) => ({
    id: application._id,
    event: application.event,
    slot: application.slot,
    application,
    occurrenceStart: application.occurrenceStart,
    variant:
      application.status === "accepted"
        ? "booked"
        : application.status === "pending"
        ? "applied"
        : "history",
  }));

const getItemSortTime = (item) => {
  const start = getSlotOccurrenceDateTime(
    item.slot?.start,
    item.occurrenceStart,
    item.event
  );
  return start?.getTime() || new Date(item.occurrenceStart).getTime();
};

const sortOpportunityItems = (items = []) =>
  [...items].sort((a, b) => getItemSortTime(a) - getItemSortTime(b));

const getOccurrenceGroupKey = (item) => {
  const eventId = item.event?._id?.toString() || "unknown";
  const occurrenceMs = new Date(item.occurrenceStart).getTime();
  return `${eventId}:${occurrenceMs}`;
};

const groupItemsByOccurrence = (items = []) => {
  const groups = new Map();
  for (const item of sortOpportunityItems(items)) {
    const groupKey = getOccurrenceGroupKey(item);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        groupKey,
        event: item.event,
        occurrenceStart: item.occurrenceStart,
        occurrenceEnd: item.occurrenceEnd,
        items: [],
      });
    }
    groups.get(groupKey).items.push(item);
  }
  return Array.from(groups.values());
};

const getGroupSortTime = (group) =>
  Math.min(...group.items.map((item) => getItemSortTime(item)));

const getEventDurationMs = (event) => {
  const start = event?.date?.start ? new Date(event.date.start) : null;
  const end = event?.date?.end ? new Date(event.date.end) : null;
  if (
    start &&
    end &&
    !Number.isNaN(start.getTime()) &&
    !Number.isNaN(end.getTime()) &&
    end > start
  ) {
    return end.getTime() - start.getTime();
  }
  return 60 * 60 * 1000;
};

const getEventOccurrenceRange = (group) => {
  const startDt = toOccurrenceDateTime(group.occurrenceStart, group.event);
  const start = startDt?.isValid ? startDt.toJSDate() : new Date(group.occurrenceStart);
  const endDt = group.occurrenceEnd
    ? toOccurrenceDateTime(group.occurrenceEnd, group.event)
    : null;
  const end = endDt?.isValid
    ? endDt.toJSDate()
    : new Date(start.getTime() + getEventDurationMs(group.event));

  if (Number.isNaN(start.getTime())) return { start: null, end: null };
  if (Number.isNaN(end.getTime()) || end <= start) {
    return { start, end: new Date(start.getTime() + getEventDurationMs(group.event)) };
  }
  return { start, end };
};

const toOccurrenceCalendarEvent = (group) => {
  const sortedItems = sortOpportunityItems(group.items);
  const firstItem = sortedItems[0];
  const { event } = group;
  const { start, end } = getEventOccurrenceRange(group);
  if (!start) return null;

  const status =
    firstItem?.application?.status || (firstItem?.variant === "open" ? "open" : null);
  const statusClass = status === "open" ? "active" : statusToCalendarClass(status);
  const countType =
    firstItem?.variant === "booked"
      ? "booked"
      : firstItem?.variant === "open"
      ? "open"
      : "applications";
  const countLabel =
    sortedItems.length > 1
      ? `${sortedItems.length} ${countType}`
      : firstItem?.slot?.title || "slot";

  return {
    id: group.groupKey,
    title: `${event?.name || "event"} · ${countLabel}`,
    start,
    end: end || new Date(start.getTime() + 60 * 60 * 1000),
    classNames: [`app-calendar-event--${statusClass}`],
    extendedProps: {
      sourceType: "artist-opportunity",
      groupKey: group.groupKey,
      item: firstItem,
      items: sortedItems,
      status,
      statusClass,
      statusLabel: status || "open",
    },
  };
};

const StatusPill = ({ status }) => (
  <span
    className={`text-kit-2xs font-['Primary-Medium'] px-kit-8 py-kit-4 rounded-kit-full ${
      statusStyle[status] || ""
    }`}
  >
    {status}
  </span>
);

const SlotDateLine = ({ event, slot, occurrenceStart }) => (
  <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
    {slot?.start
      ? formatSlotOccurrenceDate(slot.start, occurrenceStart, event)
      : formatOccurrenceDate(occurrenceStart, event)}
    {slot?.start && (
      <>
        {" · "}
        {formatSlotOccurrenceTimeRange(slot, occurrenceStart, event)}
      </>
    )}
  </div>
);

const OpportunityCardHeader = ({ event, badges = [] }) => {
  const coverImage = getEventCoverImage(event);

  return (
    <>
      {coverImage && (
        <img
          src={coverImage}
          alt=""
          className="w-[2.8rem] h-[2.8rem] rounded-kit-sm object-cover shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-['Primary-Bold'] text-kit-sm truncate">{event?.name}</div>
        <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] truncate">
          {event?.account_id?.name || event?.account_id?.username}
        </div>
      </div>
      {badges.length > 0 && (
        <div className="flex gap-kit-6 shrink-0">{badges}</div>
      )}
    </>
  );
};

const OpportunityViewToggle = ({ viewMode, onChange }) => (
  <div className="flex gap-kit-6 rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] p-kit-4">
    {[
      { id: "list", label: "list", icon: FiList },
      { id: "calendar", label: "calendar", icon: FiCalendar },
    ].map(({ id, label, icon: Icon }) => (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        className={`inline-flex items-center gap-kit-6 rounded-kit-full px-kit-12 py-kit-6 text-kit-xs font-['Primary-Medium'] transition-colors ${
          viewMode === id
            ? "bg-[var(--inyc-primary-text-color)] text-[var(--inyc-primary)]"
            : "text-[var(--inyc-primary-text-color-light)]"
        }`}
      >
        <Icon size={14} />
        {label}
      </button>
    ))}
  </div>
);

const OpportunityCalendar = ({
  items,
  onEventClick,
  onRangeChange,
  calendarView = "listWeek",
  onViewChange,
}) => {
  const calendarEvents = groupItemsByOccurrence(items)
    .sort((a, b) => getGroupSortTime(a) - getGroupSortTime(b))
    .map(toOccurrenceCalendarEvent)
    .filter(Boolean);

  return (
    <div className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] overflow-hidden">
      <AppCalendar
        events={calendarEvents}
        initialView={calendarView}
        height="auto"
        onRangeChange={onRangeChange}
        onViewChange={onViewChange}
        onEventClick={(calendarEvent) => onEventClick?.(calendarEvent.extendedProps)}
      />
    </div>
  );
};

const OpportunityList = ({
  items,
  variant,
  expandedEvent,
  onToggleEvent,
  onApply,
  onWithdraw,
  applying,
  withdrawing,
  noteInputs,
  onNoteChange,
  selectedItemId,
}) => {
  const groups = groupItemsByOccurrence(items);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-kit-20">
      {groups.map(({ groupKey, event, occurrenceStart, items: groupItems }) => {
        const isOpen = expandedEvent === groupKey;
        const occurrenceBadge = (
          <span className="text-kit-2xs font-['Primary-Medium'] bg-[var(--inyc-input-border-color)]/70 text-[var(--inyc-primary-text-color-light)] px-kit-8 py-kit-4 rounded-kit-full">
            {formatOccurrenceDate(occurrenceStart, event)}
          </span>
        );
        const badge =
          variant === "open" ? (
            <span className="text-kit-2xs font-['Primary-Medium'] bg-[var(--inyc-accent)]/20 text-[var(--inyc-accent)] px-kit-8 py-kit-4 rounded-kit-full">
              {groupItems.length} open
            </span>
          ) : variant === "booked" ? (
            <span className="text-kit-2xs font-['Primary-Medium'] bg-success-soft text-success px-kit-8 py-kit-4 rounded-kit-full">
              {groupItems.length} booked
            </span>
          ) : (
            <span className="text-kit-2xs font-['Primary-Medium'] bg-warning-soft text-warning px-kit-8 py-kit-4 rounded-kit-full">
              {groupItems.length}
            </span>
          );

        return (
          <div
            key={groupKey}
            data-group-key={groupKey}
            className={`border-[1px] rounded-kit-lg overflow-hidden ${
              groupItems.some((item) => item.id === selectedItemId)
                ? "border-[var(--inyc-accent)]"
                : "border-[var(--inyc-input-border-color)]"
            }`}
          >
            <button
              type="button"
              onClick={() => onToggleEvent(isOpen ? null : groupKey)}
              className="w-full text-left flex items-center gap-kit-12 px-kit-16 py-kit-16 bg-[var(--inyc-primary-light)] border-b-[1px] border-[var(--inyc-input-border-color)] hover:opacity-90 transition-opacity"
            >
              <OpportunityCardHeader event={event} badges={[occurrenceBadge, badge]} />
            </button>

            {isOpen && (
              <div className="divide-y divide-[var(--inyc-input-border-color)]">
                {groupItems.map((item) => {
                  const applyKey = slotOccurrenceKey(item.slot?._id, item.occurrenceStart);
                  const application = item.application;
                  const isSelected = selectedItemId === item.id;

                  return (
                    <div
                      key={item.id}
                      className={`px-kit-16 py-kit-16 ${
                        isSelected ? "bg-[var(--inyc-accent)]/5" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-kit-12">
                        <div className="flex-1 min-w-0">
                          <div className="font-['Primary-Medium'] text-kit-sm">
                            {item.slot?.title}
                          </div>
                          <SlotDateLine
                            event={item.event}
                            slot={item.slot}
                            occurrenceStart={item.occurrenceStart}
                          />
                          {application?.note && (
                            <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] mt-kit-4 italic">
                              "{application.note}"
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col items-end gap-kit-8 shrink-0">
                          {variant === "open" && (
                            <button
                              type="button"
                              onClick={() => onApply?.(item)}
                              disabled={applying === applyKey}
                              className="px-kit-16 py-kit-8 rounded-[var(--inyc-input-border-radius)] text-kit-xs font-['Primary-Medium'] bg-[var(--inyc-secondary)] text-[var(--inyc-primary)] hover:opacity-80 transition-opacity disabled:opacity-50"
                            >
                              {applying === applyKey ? "applying..." : "apply"}
                            </button>
                          )}
                          {application?.status && <StatusPill status={application.status} />}
                          {variant === "applied" && application?.status === "pending" && (
                            <button
                              type="button"
                              onClick={() => onWithdraw?.(application._id)}
                              disabled={withdrawing === application._id}
                              className="px-kit-12 py-kit-6 rounded-[var(--inyc-input-border-radius)] text-kit-xs font-['Primary-Medium'] border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:border-danger hover:text-danger transition-colors duration-150 disabled:opacity-50"
                            >
                              {withdrawing === application._id ? "..." : "withdraw"}
                            </button>
                          )}
                        </div>
                      </div>

                      {variant === "open" && (
                        <div className="mt-kit-10 ui-input-wrapper">
                          <input
                            type="text"
                            placeholder="add a note (optional)"
                            value={noteInputs[applyKey] || ""}
                            onChange={(e) => onNoteChange?.(applyKey, e.target.value)}
                            className="ui-input w-full !text-kit-xs"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const OpportunitiesPage = () => {
  const [searchParams] = useSearchParams();
  const pageFromUrl = OPPORTUNITY_PAGE_PARAMS[searchParams.get("oppPage")];
  const [page, setPage] = useState(pageFromUrl || "booked");

  useEffect(() => {
    if (pageFromUrl) setPage(pageFromUrl);
  }, [pageFromUrl]);
  return (
    <div className="flex flex-col">
      <div className="flex gap-kit-16 px-kit-24 pt-kit-16 pb-kit-8 overflow-x-auto">
        {OPPORTUNITY_PAGES.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setPage(tab)}
            className={`font-bold cursor-pointer whitespace-nowrap duration-150 text-kit-base px-kit-16 py-kit-8 rounded-kit-2xl shadow-md ${
              page === tab
                ? "bg-[var(--inyc-primary-text-color)] text-[var(--inyc-primary)]"
                : "text-[var(--inyc-primary-text-color)]/70 border-[1px] border-[var(--inyc-input-border-color)]"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      <div className="px-kit-24">
        {page === "booked" && <BookedSlotsPage />}
        {page === "applied" && <AppliedPage />}
        {page === "open slots" && <OpenSlotsPage />}
      </div>
    </div>
  );
};

const BookedSlotsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [calendarView, setCalendarView] = useState("listWeek");
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const activity = useNotificationActivity();

  const loadApplications = useCallback(() => {
    setLoading(true);
    getMyApplications()
      .then((d) => setApplications(d.applications || []))
      .catch(() => enqueueSnackbar("Failed to load booked slots", { variant: "error" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    const notification = activity?.notification;
    if (!activity?.version || notification?.scope !== "roster:opportunities") return;
    loadApplications();
  }, [activity?.version, loadApplications]);

  const items = useMemo(
    () =>
      sortOpportunityItems(
        flattenApplicationItems(applications, (app) => app.status === "accepted")
      ),
    [applications]
  );
  useOpportunityFocus({ items, loading, setExpandedEvent, setSelectedItemId });

  if (loading && applications.length === 0) {
    return (
      <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
        loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      <div className="flex items-center justify-between gap-kit-12">
        <div className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">
          your confirmed schedule
        </div>
        <OpportunityViewToggle viewMode={viewMode} onChange={setViewMode} />
      </div>

      {items.length === 0 ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
          no booked slots yet
        </div>
      ) : viewMode === "calendar" ? (
        <OpportunityCalendar
          items={items}
          calendarView={calendarView}
          onViewChange={setCalendarView}
          onEventClick={(payload) => {
            setSelectedItemId(payload?.item?.id || null);
            setExpandedEvent(payload?.groupKey || null);
          }}
        />
      ) : null}

      {(viewMode === "list" || selectedItemId) && (
        <OpportunityList
          items={items}
          variant="booked"
          expandedEvent={expandedEvent}
          onToggleEvent={setExpandedEvent}
          selectedItemId={selectedItemId}
        />
      )}
    </div>
  );
};

const AppliedPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [calendarView, setCalendarView] = useState("listWeek");
  const [searchParams] = useSearchParams();
  const filterFromUrl = APPLIED_FILTER_PARAMS[searchParams.get("appliedFilter")];
  const [filter, setFilter] = useState(filterFromUrl || "waiting");
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const activity = useNotificationActivity();

  const loadApplications = useCallback(() => {
    setLoading(true);
    getMyApplications()
      .then((d) => setApplications(d.applications || []))
      .catch(() => enqueueSnackbar("Failed to load applications", { variant: "error" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    const notification = activity?.notification;
    if (!activity?.version || notification?.scope !== "roster:opportunities") return;
    loadApplications();
  }, [activity?.version, loadApplications]);

  useEffect(() => {
    if (filterFromUrl) setFilter(filterFromUrl);
  }, [filterFromUrl]);

  const items = useMemo(() => {
    const filteredApplications =
      filter === "waiting"
        ? applications.filter((app) => app.status === "pending")
        : applications.filter((app) => ["declined", "cancelled"].includes(app.status));
    return sortOpportunityItems(flattenApplicationItems(filteredApplications));
  }, [applications, filter]);
  useOpportunityFocus({ items, loading, setExpandedEvent, setSelectedItemId });

  const handleWithdraw = async (applicationId) => {
    setWithdrawing(applicationId);
    try {
      await withdrawApplication(applicationId);
      setApplications((prev) =>
        prev.map((app) =>
          app._id === applicationId ? { ...app, status: "cancelled" } : app
        )
      );
      enqueueSnackbar("Application withdrawn", { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to withdraw", { variant: "error" });
    } finally {
      setWithdrawing(null);
    }
  };

  if (loading) {
    return (
      <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
        loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      <div className="flex items-center justify-between gap-kit-12">
        <div className="flex gap-kit-8 overflow-x-auto">
          {APPLIED_FILTERS.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => {
                setFilter(chip);
                setSelectedItemId(null);
              }}
              className={`rounded-kit-full px-kit-12 py-kit-6 text-kit-xs font-['Primary-Medium'] whitespace-nowrap ${
                filter === chip
                  ? "bg-[var(--inyc-accent)]/15 text-[var(--inyc-accent)] border-[1px] border-[var(--inyc-accent)]"
                  : "border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)]"
              }`}
            >
              {chip}
            </button>
          ))}
        </div>
        <OpportunityViewToggle viewMode={viewMode} onChange={setViewMode} />
      </div>

      {items.length === 0 ? (
        <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
          {filter === "waiting" ? "no pending applications" : "no past decisions yet"}
        </div>
      ) : viewMode === "calendar" ? (
        <OpportunityCalendar
          items={items}
          calendarView={calendarView}
          onViewChange={setCalendarView}
          onEventClick={(payload) => {
            setSelectedItemId(payload?.item?.id || null);
            setExpandedEvent(payload?.groupKey || null);
          }}
        />
      ) : null}

      {(viewMode === "list" || selectedItemId) && (
        <OpportunityList
          items={items}
          variant="applied"
          expandedEvent={expandedEvent}
          onToggleEvent={setExpandedEvent}
          onWithdraw={handleWithdraw}
          withdrawing={withdrawing}
          selectedItemId={selectedItemId}
        />
      )}
    </div>
  );
};

const OpenSlotsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [calendarView, setCalendarView] = useState("listWeek");
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [applying, setApplying] = useState(null);
  const [noteInputs, setNoteInputs] = useState({});
  const [windowFrom, setWindowFrom] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start;
  });
  const [calendarRange, setCalendarRange] = useState(null);

  const rangeFromMs = useMemo(
    () =>
      viewMode === "calendar"
        ? calendarRange?.start?.getTime() ?? windowFrom.getTime()
        : windowFrom.getTime(),
    [viewMode, calendarRange?.start, windowFrom]
  );
  const rangeToMs = useMemo(
    () =>
      viewMode === "calendar"
        ? calendarRange?.end?.getTime() ?? windowFrom.getTime() + WEEK_MS
        : windowFrom.getTime() + WEEK_MS,
    [viewMode, calendarRange?.end, windowFrom]
  );

  useEffect(() => {
    setLoading(true);
    getOpportunities({
      from: new Date(rangeFromMs),
      to: new Date(rangeToMs),
    })
      .then((d) => setEvents(d.events || []))
      .catch(() => enqueueSnackbar("Failed to load opportunities", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [rangeFromMs, rangeToMs]);

  const items = sortOpportunityItems(flattenOpenSlotItems(events));
  const windowLabel = `${formatEventDate(windowFrom)} – ${formatEventDate(
    new Date(windowFrom.getTime() + WEEK_MS)
  )}`;

  const handleApply = async (item) => {
    const applyKey = slotOccurrenceKey(item.slot._id, item.occurrenceStart);
    setApplying(applyKey);
    try {
      await applyToSlot(item.slot._id, noteInputs[applyKey] || "", item.occurrenceStart);
      enqueueSnackbar("Application submitted!", { variant: "success" });
      setEvents((prev) =>
        prev
          .map((event) => ({
            ...event,
            occurrences: (event.occurrences || [])
              .map((occurrence) => ({
                ...occurrence,
                slots: (occurrence.slots || []).filter(
                  (slot) =>
                    !(
                      slot._id?.toString() === item.slot._id?.toString() &&
                      new Date(occurrence.occurrenceStart).getTime() ===
                        new Date(item.occurrenceStart).getTime()
                    )
                ),
              }))
              .filter((occurrence) => (occurrence.slots || []).length > 0),
          }))
          .filter((event) => (event.occurrences || []).length > 0)
      );
      setNoteInputs((prev) => ({ ...prev, [applyKey]: "" }));
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to apply", { variant: "error" });
    } finally {
      setApplying(null);
    }
  };

  if (loading && events.length === 0) {
    return (
      <div className="py-kit-48 text-center text-[var(--inyc-primary-text-color-light)]">
        loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-kit-16 pt-kit-20">
      <div className="flex items-center justify-between gap-kit-12">
        {viewMode === "list" ? (
          <div className="flex items-center gap-kit-8">
            <button
              type="button"
              onClick={() => setWindowFrom((prev) => new Date(prev.getTime() - WEEK_MS))}
              className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-12 py-kit-6 text-kit-xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color-light)] hover:border-[var(--inyc-accent)]"
            >
              previous week
            </button>
            <div className="text-kit-xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color-light)]">
              {windowLabel}
            </div>
            <button
              type="button"
              onClick={() => setWindowFrom((prev) => new Date(prev.getTime() + WEEK_MS))}
              className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-12 py-kit-6 text-kit-xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color-light)] hover:border-[var(--inyc-accent)]"
            >
              next week
            </button>
          </div>
        ) : (
          <div className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">
            browse open slots in calendar
          </div>
        )}
        <OpportunityViewToggle viewMode={viewMode} onChange={setViewMode} />
      </div>

      {viewMode === "calendar" && (
        <>
          {loading && (
            <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
              refreshing open slots...
            </div>
          )}
          <OpportunityCalendar
            items={items}
            calendarView={calendarView}
            onViewChange={setCalendarView}
            onRangeChange={setCalendarRange}
            onEventClick={(payload) => {
              setSelectedItemId(payload?.item?.id || null);
              setExpandedEvent(payload?.groupKey || null);
            }}
          />
        </>
      )}

      {items.length === 0 ? (
        <div className="py-kit-64 text-center flex flex-col gap-kit-8">
          <div className="text-[var(--inyc-primary-text-color-light)] text-kit-sm">
            no open slots in this range
          </div>
          <div className="text-kit-xs text-[var(--inyc-primary-text-color-light)] opacity-70">
            join more rosters in the browse tab to see more
          </div>
        </div>
      ) : null}

      {(viewMode === "list" || selectedItemId) && (
        <OpportunityList
          items={items}
          variant="open"
          expandedEvent={expandedEvent}
          onToggleEvent={setExpandedEvent}
          onApply={handleApply}
          applying={applying}
          noteInputs={noteInputs}
          onNoteChange={(applyKey, value) =>
            setNoteInputs((prev) => ({ ...prev, [applyKey]: value }))
          }
          selectedItemId={selectedItemId}
        />
      )}
    </div>
  );
};

export default RosterPage;
